import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import {
  Card,
  SectionTitle,
  StatTile,
  Badge,
  Skeleton,
  ErrorState,
} from "../components/ui";
import {
  ClockIcon,
  WalletIcon,
  DraftingIcon,
  PinIcon,
  CheckCircleIcon,
  ReceiptIcon,
} from "../components/icons";

const QUICK_ACTIONS = [
  { to: "/attendance",   Icon: ClockIcon,    label: "Attendance",  desc: "Mark in / out" },
  { to: "/expenses/new", Icon: WalletIcon,   label: "Log Expense", desc: "Fuel or food" },
  { to: "/drawings",     Icon: DraftingIcon, label: "Drawings",    desc: "View CAD files" },
  { to: "/sites",        Icon: PinIcon,      label: "My Sites",    desc: "Site details" },
];

export default function Home() {
  const { user } = useAuth();
  const [todayAtt, setTodayAtt] = useState(null);
  const [sites, setSites] = useState([]);
  const [pendingExp, setPendingExp] = useState(0);
  const [monthStats, setMonthStats] = useState({ present: 0, totalHours: 0 });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const month = new Date().toISOString().slice(0, 7);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);

    // Each request still degrades independently (a missing "today" record is a
    // normal state, not an error), but if *every* one fails we surface it —
    // previously a dead network rendered a dashboard full of confident zeroes.
    const settle = (p) => p.then((r) => ({ ok: true, r }), () => ({ ok: false }));

    return Promise.all([
      settle(api.get("/attendance/crew/today")),
      settle(api.get("/sites/crew")),
      settle(api.get(`/expenses/crew?month=${month}`)),
      settle(api.get(`/attendance/crew?month=${month}`)),
    ])
      .then(([att, s, exp, hist]) => {
        if (![att, s, exp, hist].some((x) => x.ok)) {
          setFailed(true);
          return;
        }
        setTodayAtt(att.ok ? att.r.data.data : null);
        setSites(s.ok ? s.r.data.data || [] : []);
        setPendingExp(
          exp.ok ? (exp.r.data.data || []).filter((e) => e.status === "pending").length : 0
        );
        const records = hist.ok ? hist.r.data.data || [] : [];
        setMonthStats({
          present: records.filter((r) => r.status === "present").length,
          totalHours: +records.reduce((s, r) => s + (r.workHours || 0), 0).toFixed(1),
        });
      })
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <header>
        <p className="text-sm text-slate-500">{greeting},</p>
        <h1 className="mt-0.5 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          {firstName} <span aria-hidden="true">👋</span>
        </h1>
        {user?.role && (
          <p className="mt-1.5">
            <Badge tone="green">{user.role}</Badge>
          </p>
        )}
      </header>

      {failed && !loading ? (
        <ErrorState message="Couldn't load your dashboard." onRetry={load} />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-start">
          {/* ── Main column ──────────────────────────────── */}
          <div className="space-y-6 lg:col-span-2">
            <TodayCard loading={loading} record={todayAtt} />

            <section aria-label="This month">
              <SectionTitle>This Month</SectionTitle>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatTile
                  icon={CheckCircleIcon}
                  label="Days present"
                  value={monthStats.present}
                  tone="green"
                  to="/attendance"
                  loading={loading}
                />
                <StatTile
                  icon={ClockIcon}
                  label="Hours logged"
                  value={`${monthStats.totalHours}h`}
                  tone="blue"
                  to="/attendance"
                  loading={loading}
                />
                <StatTile
                  icon={PinIcon}
                  label="Assigned sites"
                  value={sites.length}
                  tone="slate"
                  to="/sites"
                  loading={loading}
                />
                <StatTile
                  icon={ReceiptIcon}
                  label="Pending expenses"
                  value={pendingExp}
                  tone={pendingExp > 0 ? "amber" : "slate"}
                  to="/expenses"
                  loading={loading}
                />
              </div>
            </section>

            <section aria-label="Quick actions">
              <SectionTitle>Quick Actions</SectionTitle>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {QUICK_ACTIONS.map(({ to, Icon, label, desc }) => (
                  <Link
                    key={to}
                    to={to}
                    className="group rounded-2xl border border-slate-200 bg-white p-4 shadow-card transition-all hover:border-brand-300 hover:shadow-lift active:scale-[0.97]"
                  >
                    <span className="mb-2.5 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="block font-display text-sm font-semibold text-slate-900 group-hover:text-brand-800">
                      {label}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500">{desc}</span>
                  </Link>
                ))}
              </div>
            </section>
          </div>

          {/* ── Side column (stacks under main on < lg) ───── */}
          <aside aria-label="My sites">
            <SectionTitle
              action={
                sites.length > 3 ? (
                  <Link
                    to="/sites"
                    className="text-xs font-semibold text-brand-700 hover:text-brand-800 hover:underline"
                  >
                    See all →
                  </Link>
                ) : null
              }
            >
              My Sites
            </SectionTitle>

            {loading ? (
              <div className="space-y-2.5">
                <Skeleton className="h-[68px] rounded-2xl" />
                <Skeleton className="h-[68px] rounded-2xl" />
              </div>
            ) : sites.length === 0 ? (
              <Card className="p-5 text-center">
                <p className="text-sm text-slate-500">No sites assigned yet.</p>
                <p className="mt-1 text-xs text-slate-400">Contact your supervisor.</p>
              </Card>
            ) : (
              <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-1">
                {sites.slice(0, 5).map((site) => (
                  <li key={site._id}>
                    <Link
                      to={`/sites/${site._id}`}
                      className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-card transition-all hover:border-brand-300 hover:shadow-lift active:scale-[0.99]"
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                        <PinIcon className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-2 block text-sm font-semibold text-slate-900">
                          {site.name}
                        </span>
                        <span className="block truncate text-xs text-slate-500">
                          {site.code || site.city || "—"}
                        </span>
                      </span>
                      <Badge tone={site.status === "active" ? "green" : "slate"} className="shrink-0">
                        {site.status}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Today's attendance banner — the single most important thing on
   this screen, so it gets the full width and the largest target.
───────────────────────────────────────────────────────────── */
function TodayCard({ loading, record }) {
  if (loading) return <Skeleton className="h-[132px] rounded-2xl" />;

  const dateLabel = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  // Not checked in — the whole card is the target.
  if (!record) {
    return (
      <Link
        to="/attendance"
        className="flex items-center justify-between gap-4 rounded-2xl border-2 border-dashed border-brand-300 bg-brand-50/60 p-5 transition-colors hover:bg-brand-50 active:scale-[0.99]"
      >
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Today</p>
          <p className="mt-1 font-display text-lg font-bold text-slate-900">Not checked in yet</p>
          <p className="mt-0.5 text-sm text-slate-500">{dateLabel}</p>
        </div>
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand-700 text-white shadow-sm">
          <ClockIcon className="h-7 w-7" />
        </span>
      </Link>
    );
  }

  const done = !!record.checkOut;

  return (
    <div
      className={`rounded-2xl border p-5 shadow-card ${
        done ? "border-slate-200 bg-white" : "border-brand-200 bg-brand-50"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Today</p>
          <p className="mt-1 flex items-center gap-2">
            <span
              aria-hidden="true"
              className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                done ? "bg-slate-400" : "bg-brand-600 pulse-green"
              }`}
            />
            <span
              className={`font-display text-lg font-bold ${
                done ? "text-slate-700" : "text-brand-800"
              }`}
            >
              {done ? "Checked out" : "Currently working"}
            </span>
          </p>
          <p className="mt-1 font-mono text-sm text-slate-600 tabular">
            In {record.checkIn}
            {done && ` · Out ${record.checkOut} · ${record.workHours}h`}
          </p>
        </div>

        {!done && (
          <Link
            to="/attendance"
            className="inline-flex h-12 shrink-0 items-center rounded-xl bg-brand-700 px-5 font-display text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-800 active:scale-[0.98]"
          >
            Check out →
          </Link>
        )}
      </div>
    </div>
  );
}
